import { getDefaultPasswordLabels, type PasswordRequirement } from "../types";

/**
 * Password validation labels type
 * Allows custom labels for i18n support
 */
export interface PasswordValidationLabels {
  minLength: string;
  uppercase: string;
  lowercase: string;
  number: string;
}

/**
 * Check if password has minimum length (8 characters)
 * @param password - The password to check
 * @returns true if password has at least 8 characters
 */
export const hasMinLength = (password: string): boolean => password.length >= 8;

/**
 * Check if password has at least one uppercase letter
 * @param password - The password to check
 * @returns true if password has at least one uppercase letter
 */
export const hasUppercase = (password: string): boolean =>
  /[A-Z]/.test(password);

/**
 * Check if password has at least one lowercase letter
 * @param password - The password to check
 * @returns true if password has at least one lowercase letter
 */
export const hasLowercase = (password: string): boolean =>
  /[a-z]/.test(password);

/**
 * Check if password has at least one number
 * @param password - The password to check
 * @returns true if password has at least one number
 */
export const hasNumber = (password: string): boolean => /\d/.test(password);

/**
 * Get password requirements with validation status
 *
 * @param password - The password to validate
 * @param labels - Optional custom labels for i18n support
 * @returns Array of password requirements with validation status
 *
 * @example
 * ```ts
 * // With default labels
 * const requirements = getPasswordRequirements("MyPass123");
 *
 * // With custom i18n labels
 * const requirements = getPasswordRequirements("MyPass123", {
 *   minLength: t("auth:passwordRequirements.minLength"),
 *   uppercase: t("auth:passwordRequirements.uppercase"),
 *   lowercase: t("auth:passwordRequirements.lowercase"),
 *   number: t("auth:passwordRequirements.number"),
 * });
 * ```
 */
export const getPasswordRequirements = (
  password: string,
  labels?: PasswordValidationLabels,
): PasswordRequirement[] => {
  const effectiveLabels = labels ?? getDefaultPasswordLabels();

  return [
    {
      label: effectiveLabels.minLength,
      isValid: hasMinLength(password),
    },
    {
      label: effectiveLabels.uppercase,
      isValid: hasUppercase(password),
    },
    {
      label: effectiveLabels.lowercase,
      isValid: hasLowercase(password),
    },
    {
      label: effectiveLabels.number,
      isValid: hasNumber(password),
    },
  ];
};

/**
 * Validate password meets all requirements
 *
 * @param password - The password to validate
 * @returns true if password meets all requirements
 */
export const validatePassword = (password: string): boolean => {
  return (
    hasMinLength(password) &&
    hasUppercase(password) &&
    hasLowercase(password) &&
    hasNumber(password)
  );
};

/**
 * Get validation errors for password
 * Returns array of failed requirement labels
 *
 * @param password - The password to validate
 * @param labels - Optional custom labels for i18n support
 * @returns Array of failed requirement labels
 */
export const getPasswordErrors = (
  password: string,
  labels?: PasswordValidationLabels,
): string[] => {
  const requirements = getPasswordRequirements(password, labels);
  return requirements.filter((req) => !req.isValid).map((req) => req.label);
};
